import { type ReactNode, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { LogOut, Menu, Search, User2 } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ArbiterSidebar } from "@/components/ArbiterSidebar";
import { CommandPalette } from "@/components/CommandPalette";
import { NotificationsDrawer } from "@/components/NotificationsDrawer";
import { supabase } from "@/lib/supabase";
import { markIntentionalSignout } from "@/lib/intentionalSignout";

interface ArbiterShellProps {
  children: ReactNode;
  /** Panneau Assistant IA (ou autre) en colonne de droite. Si non fourni, la 3e colonne est masquee. */
  rightPanel?: ReactNode;
  /** Mode focus delibere : masque sidebar + panneau IA pour la pleine largeur centrale. */
  focused?: boolean;
  /** Titre court de la page courante (breadcrumb top bar). */
  title?: string;
  /** Slot personnalise dans la top bar (ex : bouton Focus Mode). */
  topBarExtra?: ReactNode;
}

// Layout 3 panneaux Premium Workspace Arbitre.
//
// Pose `data-workspace="arbitre"` sur le wrapper racine -> active le
// theme dark AAA + tokens graphite/proof-gold + Inter par defaut + cible
// tactile 44px sur les boutons shadcn (cf. Phase A index.css).
//
// Disposition reactive :
//   - >= 2xl (1536px) : sidebar 280 | center | aside 360
//   - >= xl  (1280px) : sidebar 260 | center | aside 320
//   - >= lg  (1024px) : sidebar 240 | center  (panneau IA cache)
//   - <  lg           : sidebar via Sheet drawer, panneau IA cache
//
// Raccourci ⌘K / Ctrl+K -> CommandPalette pour navigation rapide entre
// dossiers et pages app. Le listener est attache au document tant que
// le shell est monte.
export function ArbiterShell({
  children,
  rightPanel,
  focused,
  title,
  topBarExtra,
}: ArbiterShellProps) {
  const [, setLocation] = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onSignOut = () => {
    // Marque le signout comme volontaire AVANT l'appel async, sinon
    // l'AuthContext declenche le magic link auto-recovery en pensant
    // a un SIGNED_OUT involontaire (Safari ITP, etc.).
    markIntentionalSignout();
    void supabase.auth.signOut();
  };

  return (
    <div
      data-workspace="arbitre"
      className="min-h-screen flex flex-col bg-background text-foreground"
    >
      {/* === TOP BAR ===================================================== */}
      <header
        className="sticky top-0 z-30 flex items-center gap-1 sm:gap-2 border-b border-border px-3 sm:px-4 h-12 backdrop-blur-sm"
        style={{ background: "color-mix(in oklch, var(--graphite-900) 92%, transparent)" }}
      >
        {!focused && (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Ouvrir la navigation"
              >
                <Menu size={18} />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[280px] p-0"
              style={{ background: "var(--graphite-900)" }}
            >
              <ArbiterSidebar onNavigate={() => setMobileNavOpen(false)} />
            </SheetContent>
          </Sheet>
        )}

        <div className="flex-1 min-w-0">
          {title && (
            <h1
              className="text-sm font-semibold truncate"
              style={{ letterSpacing: "-0.01em" }}
            >
              {title}
            </h1>
          )}
        </div>

        {topBarExtra}

        <Button
          variant="ghost"
          size="icon"
          className="hidden sm:inline-flex"
          onClick={() => setPaletteOpen(true)}
          aria-label="Recherche rapide (Cmd+K)"
          title="Cmd+K"
        >
          <Search size={16} />
        </Button>

        {!focused && <NotificationsDrawer />}

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/profil")}
          aria-label="Mon profil"
        >
          <User2 size={16} />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={onSignOut}
          aria-label="Se deconnecter"
        >
          <LogOut size={16} />
        </Button>
      </header>

      {/* === GRID 3 PANNEAUX ============================================ */}
      <div className="flex-1 flex overflow-hidden">
        {!focused && (
          <aside
            className="hidden lg:flex flex-col w-[240px] xl:w-[260px] 2xl:w-[280px] border-r border-border overflow-y-auto shrink-0"
            style={{ background: "var(--graphite-900)" }}
            aria-label="Navigation principale"
          >
            <ArbiterSidebar />
          </aside>
        )}

        <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
          {children}
        </main>

        {rightPanel && !focused && (
          <aside
            className="hidden xl:flex flex-col w-[320px] 2xl:w-[360px] border-l border-border overflow-y-auto shrink-0"
            style={{ background: "var(--graphite-800)" }}
            aria-label="Assistant IA"
          >
            {rightPanel}
          </aside>
        )}
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
